import { memo, useEffect, useState } from "react";
import { resolveApiUrl } from "~/utils/url/urlResolver";
import throttle from "lodash/throttle";
import { SessionExpirePopup } from "~/features/auth/session/SessionExpirePopup";
import { doLogout } from "~/features/auth/api/doLogout";
import { userIsLoggedIn } from "~/domains/user/utils/roles";
import { SessionExpiredPopup } from "~/features/auth/session/SessionExpiredPopup";

const sessionTimeout = window.SCHA.config.sessionTimeout ?? 20;
const autoLogoutMessageTime = window.SCHA.config.autoLogoutMessageTime ?? 10;

const STORAGE_PARAM_EXPIRED_SESSION = "sessionExpired";

export const useSessionExpirePopup = () => {
  const [expiredPopupVisible, setExpiredPopupVisible] = useState(
    window.sessionStorage.getItem(STORAGE_PARAM_EXPIRED_SESSION) === "true",
  );
  const [popupVisible, setPopupVisible] = useState(false);
  const [timeout, setTimeout] = useState(0);
  const [popupConfig, setPopupConfig] = useState(() => {
    return {
      onClose: () => {},
      onLogout: () => {},
      onRefresh: () => {},
    };
  });

  useEffect(() => {
    if (expiredPopupVisible) {
      window.sessionStorage.setItem(STORAGE_PARAM_EXPIRED_SESSION, "");
      return;
    }
    if (!userIsLoggedIn()) {
      return;
    }

    // Idle time to show the popup
    const idleToShowPopup = sessionTimeout - autoLogoutMessageTime;
    // Time after popup is open before logout happens
    const timeToLogout = autoLogoutMessageTime;

    let intervalId = -1;
    let time = 0;
    setTimeout(sessionTimeout);

    function resetTimer() {
      time = 0;
      clearInterval(intervalId);
      intervalId = setInterval(countIdle, 1000) as unknown as number;
      throttledReset();
    }

    const addListeners = () => events.forEach((event) => window.addEventListener(event, resetTimer));
    const removeListeners = () => events.forEach((event) => window.removeEventListener(event, resetTimer));

    function countIdle() {
      time += 1;
      setTimeout(sessionTimeout - time);

      if (time === idleToShowPopup) {
        removeListeners();
        const refreshAndClose = async () => {
          resetTimer();
          resetBETimer();
          addListeners();
          setPopupVisible(false);
        };

        setPopupConfig((prev) => ({
          ...prev,
          onLogout: async () => {
            await doLogout();
            setPopupVisible(false);
          },
          onRefresh: refreshAndClose,
          onClose: refreshAndClose,
        }));
        setPopupVisible(true);
      }
      if (time === sessionTimeout) {
        clearInterval(intervalId);
        doLogout(false).then(() => {
          window.sessionStorage.setItem(STORAGE_PARAM_EXPIRED_SESSION, "true");
          window.location.reload();
        });
      }
    }
    addListeners();
    resetTimer();

    return removeListeners;
  }, []);

  let cmp = null;

  if (popupVisible) {
    cmp = <SessionExpirePopup {...popupConfig} timeout={timeout} />;
  }

  if (expiredPopupVisible) {
    cmp = <SessionExpiredPopup />;
  }

  return cmp;
};

const events = ["load", "mousemove", "mousedown", "touchstart", "click", "keypress"];

const resetBETimer = () => fetch(resolveApiUrl("/refresh-session"));
const throttledReset = throttle(resetBETimer, 1000 * 60, { leading: false });

export const SessionExpirePopupContainer = memo(() => {
  return useSessionExpirePopup();
});
